import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Building2, Users, FileText, ArrowRight, CheckCircle2, BarChart3 } from 'lucide-react';
import { SimonsHeader, SimonsFooter } from '../components/SimonsNavigation';
export function SimonsCommercial({
  onBack,
  setActivePage
}: {
  onBack: () => void;
  setActivePage: (p: any) => void;
}) {
  return <div className="min-h-screen bg-white text-[#002366] font-sans" data-id="element-1708">
      <SimonsHeader activePage="commercial" setActivePage={setActivePage} data-id="element-1709" />

      {/* Hero Section */}
      <section className="bg-[#002366] text-white py-24 px-8 relative overflow-hidden" data-id="element-1710">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10" data-id="element-1711">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} data-id="element-1712">
            <h1 className="text-5xl lg:text-7xl font-black mb-8 leading-tight tracking-tighter" data-id="element-1713">
              Enterprise Risk <br data-id="element-1714" />
              <span className="text-[#FFD700]" data-id="element-1715">Assessment.</span>
            </h1>
            <p className="text-xl text-white/60 mb-10 max-w-xl font-medium" data-id="element-1716">
              Strategic B2B protection designed for operational resilience. We
              analyze your exposure to provide institutional-grade coverage.
            </p>
            <div className="flex gap-4" data-id="element-1717">
              <button className="px-8 py-4 bg-[#D32F2F] text-white rounded-xl font-bold hover:bg-[#B71C1C] transition-all shadow-xl shadow-[#D32F2F]/20" data-id="element-1718">
                Request Assessment
              </button>
              <button className="px-8 py-4 bg-white/10 border border-white/20 rounded-xl font-bold hover:bg-white/20 transition-all" data-id="element-1719">
                View Capabilities
              </button>
            </div>
          </motion.div>
          <div className="hidden lg:block relative" data-id="element-1720">
            <div className="bg-white/5 rounded-[32px] p-8 border border-white/10 backdrop-blur-sm" data-id="element-1721">
              <div className="space-y-6" data-id="element-1722">
                <div className="h-4 bg-white/20 rounded w-3/4" data-id="element-1723"></div>
                <div className="h-4 bg-white/20 rounded w-1/2" data-id="element-1724"></div>
                <div className="grid grid-cols-3 gap-4 pt-4" data-id="element-1725">
                  <div className="h-20 bg-[#FFD700]/20 rounded-xl border border-[#FFD700]/30" data-id="element-1726"></div>
                  <div className="h-20 bg-white/10 rounded-xl" data-id="element-1727"></div>
                  <div className="h-20 bg-white/10 rounded-xl" data-id="element-1728"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-1/2 h-full bg-[#001a4d] -skew-x-12 translate-x-1/4" data-id="element-1729"></div>
      </section>

      {/* 3-Column Grid */}
      <section className="py-24 px-8 max-w-7xl mx-auto" data-id="element-1730">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1731">
          {[{
          title: 'General Liability',
          icon: Shield,
          desc: 'Comprehensive protection against third-party claims and operational risks.'
        }, {
          title: 'Commercial Property',
          icon: Building2,
          desc: 'Safeguarding your physical assets, inventory, and business locations.'
        }, {
          title: 'Workers Comp',
          icon: Users,
          desc: 'Statutory coverage designed to protect your most valuable asset: your people.'
        }].map((item, i) => <div key={i} className="p-10 bg-[#F3F6F9] rounded-[16px] border border-slate-200 hover:border-[#002366] transition-all group" data-id="element-1732">
              <div className="w-14 h-14 bg-white rounded-2xl flex items-center justify-center text-[#002366] mb-8 shadow-sm group-hover:bg-[#002366] group-hover:text-white transition-all" data-id="element-1733">
                <item.icon size={28} data-id="element-1734" />
              </div>
              <h3 className="text-2xl font-black mb-4" data-id="element-1735">{item.title}</h3>
              <p className="text-slate-500 font-medium leading-relaxed mb-6" data-id="element-1736">
                {item.desc}
              </p>
              <button className="text-sm font-black uppercase tracking-widest text-[#002366] flex items-center gap-2" data-id="element-1737">
                Learn More <ArrowRight size={16} data-id="element-1738" />
              </button>
            </div>)}
        </div>
      </section>

      {/* Lead Magnet */}
      <section className="py-24 px-8" data-id="element-1739">
        <div className="max-w-5xl mx-auto bg-[#002366] rounded-[32px] p-16 text-white flex flex-col md:flex-row items-center justify-between gap-12" data-id="element-1740">
          <div className="max-w-md" data-id="element-1741">
            <h2 className="text-3xl font-black mb-4" data-id="element-1742">
              Operational Resilience: A Case Study
            </h2>
            <p className="text-white/60 font-medium mb-8" data-id="element-1743">
              How we saved a mid-market logistics firm 24% on premiums while
              increasing coverage limits.
            </p>
            <button className="flex items-center gap-3 px-6 py-3 bg-[#FFD700] text-[#002366] rounded-xl font-black uppercase tracking-widest text-xs hover:bg-white transition-all" data-id="element-1744">
              <FileText size={18} data-id="element-1745" /> Download Case Study
            </button>
          </div>
          <div className="w-full md:w-64 aspect-[3/4] bg-white/10 rounded-2xl border border-white/20 flex items-center justify-center" data-id="element-1746">
            <FileText size={64} className="text-white/20" data-id="element-1747" />
          </div>
        </div>
      </section>

      {/* Enterprise CTA Form */}
      <section className="py-24 px-8 bg-slate-50" data-id="element-1748">
        <div className="max-w-4xl mx-auto bg-white rounded-[40px] border border-slate-200 shadow-2xl p-12" data-id="element-1749">
          <div className="text-center mb-12" data-id="element-1750">
            <h2 className="text-4xl font-black text-[#002366] mb-4 tracking-tight" data-id="element-1751">
              Request Enterprise Consultation
            </h2>
            <p className="text-slate-500 font-medium" data-id="element-1752">
              Schedule a strategic risk assessment with our senior architects.
            </p>
          </div>
          <form className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-1753">
            <div className="space-y-2" data-id="element-1754">
              <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-1755">
                Company Name
              </label>
              <input type="text" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 focus:outline-none focus:border-[#002366]" placeholder="Global Logistics Inc." data-id="element-1756" />
            </div>
            <div className="space-y-2" data-id="element-1757">
              <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-1758">
                Industry
              </label>
              <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 focus:outline-none focus:border-[#002366]" data-id="element-1759">
                <option data-id="element-1760">Transportation & Logistics</option>
                <option data-id="element-1761">Real Estate Portfolio</option>
                <option data-id="element-1762">Manufacturing</option>
                <option data-id="element-1763">Technology</option>
              </select>
            </div>
            <div className="space-y-2" data-id="element-1764">
              <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-1765">
                Work Email
              </label>
              <input type="email" className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 focus:outline-none focus:border-[#002366]" placeholder="exec@company.com" data-id="element-1766" />
            </div>
            <div className="space-y-2" data-id="element-1767">
              <label className="text-[10px] font-black uppercase text-slate-400" data-id="element-1768">
                Request Type
              </label>
              <select className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-4 focus:outline-none focus:border-[#002366]" data-id="element-1769">
                <option data-id="element-1770">Book a Demo</option>
                <option data-id="element-1771">Request Callback</option>
                <option data-id="element-1772">Custom Risk Audit</option>
              </select>
            </div>
            <button className="md:col-span-2 py-5 bg-[#DC143C] text-white rounded-xl font-black text-lg shadow-xl shadow-[#DC143C]/20 hover:bg-[#B21031] transition-all" data-id="element-1773">
              Submit Request
            </button>
          </form>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 px-8 bg-slate-50 border-t border-slate-200" data-id="element-1774">
        <div className="max-w-7xl mx-auto text-center" data-id="element-1775">
          <p className="text-xs font-black uppercase tracking-[0.3em] text-slate-400 mb-12" data-id="element-1776">
            Institutional Partners
          </p>
          <div className="flex flex-wrap justify-center gap-16 opacity-30 grayscale" data-id="element-1777">
            <div className="h-8 w-32 bg-slate-400 rounded" data-id="element-1778"></div>
            <div className="h-8 w-32 bg-slate-400 rounded" data-id="element-1779"></div>
            <div className="h-8 w-32 bg-slate-400 rounded" data-id="element-1780"></div>
            <div className="h-8 w-32 bg-slate-400 rounded" data-id="element-1781"></div>
          </div>
        </div>
      </section>

      <SimonsFooter data-id="element-1782" />
    </div>;
}